import axios from "axios";
import store from "../store";
import settings from "../settings";
import Vue from "vue";
import Common from "../services/Common";

import QS from "qs";
import jwtDecode from "jwt-decode";

import * as rax from "retry-axios";
//import { isNull } from "lodash";

const ajax = axios.create({
  baseURL: settings.baseUrlApi,
});

ajax.defaults.raxConfig = {
  retry: 5,
  noResponseRetries: 5,
  instance: ajax,
  onRetryAttempt: (err) => {
    const cfg = rax.getConfig(err);
    console.debug(`Retry attempt #${cfg.currentRetryAttempt}`);
  },
  retryDelay: 2000,
};
rax.attach(ajax);

const noRetryConfig = ajax.defaults.raxConfig;
noRetryConfig.retry = 0;

function logout() {
  store.commit("logout");
}

ajax.defaults.headers.common["Authorization"] = store.state.token;

ajax.interceptors.request.use((request) => {
  if (
    request.headers.common.Authorization &&
    request.url.toLowerCase().indexOf("authentication")
  ) {
    var decodedToken = jwtDecode(request.headers.common.Authorization);
    if (decodedToken.exp < new Date().getTime() / 1000) {
      logout("Login timed out");
    }
  }
  return request;
});

ajax.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    console.log('API Error:', error.response?.data);

    if (error.response && error.response.status === 401) {
      logout("Login timed out");
    } else if (error.response && error.response.status === 403) {
      store.commit("ipForbidden");
    } else if (
      error.response?.status === 500 && (
        error.response?.data?.detail?.includes('IDX10503') ||
        error.response?.data?.detail?.toLowerCase().includes('token validation failed') ||
        error.response?.data?.detail?.toLowerCase().includes('aad') ||
        error.response?.data?.detail?.toLowerCase().includes('msal') ||
        error.response?.data?.title?.toLowerCase().includes('authentication failed')
      )
    ) {
      console.log('Token/Authentication error detected - setting authError state');
      store.commit('setAuthError', true);
      Vue.$toast.error("Login issue detected. Click 'Refresh Authentication Data' to potentially resolve the problem.");
  
    } else if (error.response && error.response.data) {
      const responseData = error.response.data;

      // Check if data is a Blob containing JSON (likely from a download request error)
      if (responseData instanceof Blob && responseData.type === 'application/json') {
          responseData.text().then(jsonText => {
              try {
                  const parsedData = JSON.parse(jsonText);
                  const message = parsedData.detail || parsedData.message || "An error occurred processing the response.";
                  Vue.$toast.error(message); 
              } catch (parseError) {
                  Vue.$toast.error("Received an invalid error format.");
              }
          }).catch(readError => {
              Vue.$toast.error("Failed to read error details.");
          });
          // We handle the toast above, so we don't fall through to Common.readError for JSON blobs.

      } else if (responseData.lockedById === undefined) {
          Vue.$toast.error(Common.readError(error));
      }
    } 

    return Promise.reject(error);
  }
);

export default {
  async refreshPermissions() {
    const resp = await ajax.get("authentication/refresh");
    if (resp.status === 200) {
      store.commit("refreshPermissions", resp.data);
    }
    return resp;
  },
  checkIpFilter(email) {
    return ajax.options("authentication", {
      params: { email },
    });
  },
  getUISettings() {
    return ajax.get("settings");
  },

  //GET
  getEsvMinimal(id) {
    return ajax.get("ESV/Minimal", {
      params: { id },
    });
  },
  getEsvs(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    ignoreServiceLimitTime
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("esv", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        ignoreServiceLimitTime,
      },
    });
  },
  getESVDatatable(
    url,
    {
      options: { page, itemsPerPage, sortBy, sortDesc },
      search,
      ignoreServiceLimitTime,
    }
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get(url, {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        ignoreServiceLimitTime,
      },
    });
  },
  getUnits({ options: { page, itemsPerPage, sortBy, sortDesc }, search, id }) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("units", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        id,
      },
    });
  },
  getEsvOverview(searchOptions) {
    return this.getESVDatatable("ESV/Overview", searchOptions);
  },
  getEsvRnsOverview(searchOptions) {
    return this.getESVDatatable("ESV/OverviewEsvsAndRns", searchOptions);
  },
  getCommunicationRules(searchOptions) {
    return this.getESVDatatable("ECareCommunicationRules/CommunicationRules", searchOptions);
  },
getECareApiLogs(params) {
  return ajax.get("ECareLogs", { params });
},
  getEsvInprogress(searchOptions) {
    return this.getESVDatatable("ESV/InProgress", searchOptions);
  },
  getEsvsToBeApproved(searchOptions) {
    return this.getESVDatatable("ESV/ToBeApproved", searchOptions);
  },
  getEsvsToBeDistributed(searchOptions) {
    return this.getESVDatatable("ESV/ToBeDistributed", searchOptions);
  },
  getUnderPreparation(searchOptions) {
    return this.getESVDatatable("ESV/UnderPreparation", searchOptions);
  },
  getLatestUpdates() {
    return ajax.get("ESV/LatestUpdates");
  },
  getEsvSituations(searchOptions) {
    return this.getESVDatatable("situation/overview", searchOptions);
  },
  getAdvancedSearchData(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    ignoreServiceLimitTime
  ) {
    //var search = this.mergeTwoArraysTogetherToAnObject(searchTable, searchAdvanced);
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("search/advancedSearchData", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        ignoreServiceLimitTime,
      },
    });
  },
  getAdvancedSearch() {
    return ajax.get("search/advancedsearch");
  },
  getPartNames(nameInitial) {
    return ajax.get("search/searchCandidatePartNames", {
      params: {
        nameInitial,
      },
    });
  },
  getModelNames(nameInitial) {
    return ajax.get("search/searchCandidateModelNames", {
      params: {
        nameInitial,
      },
    });
  },
  getGlobalSearch(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    dynamicInput,
    ignoreServiceLimitTime
  ) {
    var searchKeys = Object.keys(search);
    searchKeys.push("dynamicSearch");
    var searchValues = this.mapSearchValues(search);
    searchValues.push(dynamicInput);

    return ajax.get("search/dynamicSearch", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        ignoreServiceLimitTime,
      },
    });
  },
  getSerialSearchData(
    { page, itemsPerPage, sortBy, sortDesc, ignoreServiceLimitTime },
    searchTable,
    searchSerial
  ) {
    var search = this.mergeTwoArraysTogetherToAnObject(
      searchTable,
      searchSerial
    );
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("search/serialSearch", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        ignoreServiceLimitTime,
      },
    });
  },
  // getIpWhiteList(search) {
  //   return ajax.get('ipwhitelist', {
  //     params: {
  //       search
  //     }
  //   })
  // },
  getOrganisations() {
    return ajax.get("dropdown/organisation");
  },
  getActions() {
    return ajax.get("Dropdown/LoggedUpdateTypes");
  },
  getResponsibleParties() {
    return ajax.get("dropdown/responsibleparty");
  },
  getUsers() {
    return ajax.get("users");
  },
  getUserDetails(userId) {
    return ajax.get("users/details", {
      params: {
        userId,
      },
    });
  },
  getUserProfileDetails() {
    return ajax.get("users/profile");
  },
  getEmptyUser() {
    return ajax.get("users/emptyUser");
  },
  getUserOverview(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    showAll,
    organisationId
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("users/UserOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        showAll,
        organisationId,
      },
    });
  },
  resendInvitationMail(userId) {
    return ajax.get("users/resendInvitationMail", {
      params: { userId },
    });
  },
  getOrganisationsOverview({ page, itemsPerPage, sortBy, sortDesc }, search) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("organisation/OrganisationOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getOrganisationDetails(organisationId) {
    return ajax.get("organisation/details", {
      params: {
        organisationId,
      },
    });
  },
  getOrganisationEsvOverview(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    organisationId
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("organisation/OrganisationEsvOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        organisationId,
      },
    });
  },
  getEmptyOrganisation() {
    return ajax.get("organisation/emptyOrganisation");
  },
  getCountries() {
    return ajax.get("DropDown/Countries");
  },
  getSBMProfiles() {
    return ajax.get("ECareCommunicationRules/SBMProfiles");
  },
  getEsvDetails(id) {
    return ajax.get("esv/details", {
      params: {
        id,
        selectedCultureId: store.state.user.selectedCultureId,
      },
    });
  },
  getAppliedModelsXModelsOverview(options, search) {
    if (search !== null && search !== undefined) {
      options.searchKeys = Object.keys(search);
      options.searchValues = this.mapSearchValues(search);
    }

    return ajax.get("appliedmodelsxmodels/overview", {
      params: options,
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
    });
  },
  updateFieldSettings(esvId, fieldSettings) {
    return ajax.put("appliedmodelsxmodels/fieldsettings", fieldSettings, {
      params: { esvId }
    });
  },
  getAvailableAppliedModelsXModelsOverview(esvId, search, page = 1, pageSize = 500, appliedModelId = null) {
    const params = { esvId, search, page, pageSize };
    if (appliedModelId !== null && appliedModelId !== undefined) {
      params.appliedModelId = appliedModelId;
    }

    return ajax.get("appliedmodelsxmodels/availableOverview", {
      params,
    });
  },
  getParts(esvId) {
    return ajax.get("Parts", {
      params: {
        esvId,
      },
    });
  },
  getPartsForSelect(esvId, appliedModelId) {
    return ajax.get("parts/select", {
      params: {
        esvId,
        appliedModelId,
      },
    });
  },
  getProductGroups() {
    return ajax.get("Dropdown/ProductGroup");
  },
  getProductSubGroups(productGroupId) {
    return ajax.get("Dropdown/ProductSubGroup", {
      params: { productGroupId },
    });
  },
  getProductSubGroupsMulti(productGroupIds) {
    const params = new URLSearchParams();

    productGroupIds.forEach(id => {
      params.append("productGroupIds", id);
    });

    return ajax.get("Dropdown/ProductSubGroups?" + params.toString());
  },
  getAllowedInfoLevels() {
    return ajax.get("/ECareCommunicationRules/InfoLevels");
  },
  getStatusses() {
    return ajax.get("Dropdown/Status");
  },
  getServiceRequestTypes() {
    return ajax.get("Dropdown/ServiceRequestType");
  },
  getServiceGrades() {
    return ajax.get("Dropdown/ServiceGrades");
  },
  getCurrencies() {
    return ajax.get("Dropdown/Currencies");
  },
  getAppliedModelsXModel(id) {
    return ajax.get("appliedmodelsxmodels", {
      params: {
        id,
      },
    });
  },
  getServiceCosts(esvId) {
    return ajax.get("appliedmodels/ServiceCosts", {
      params: {
        esvId,
      },
    });
  },
  getAppliedModels(esvId) {
    return ajax.get("appliedmodels/overview", {
      params: {
        esvId,
      },
    });
  },
  getAppliedModel(id) {
    return ajax.get("appliedmodels", {
      params: {
        id,
      },
    });
  },
  getModelsForAppliedModel(
    { id, page, itemsPerPage, sortBy, sortDesc, showAll },
    search,
    skipFilter
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("appliedmodelsxmodels/appliedmodel", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        id,
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        showAll,
        skipFilter,
      },
    });
  },
  getSparePart(id, esvId, partId) {
    return ajax.get("parts/details", {
      params: {
        id,
        esvId,
        partId,
      },
    });
  },
  getPartsForAppliedModel(appliedModelId) {
    return ajax.get("parts/appliedmodel", {
      params: {
        appliedModelId,
      },
    });
  },
  getInternalDocuments(id) {
    return ajax.get("ESV/internaldocuments", {
      params: {
        id,
      },
    });
  },
  getDetailedOrganisationOverview() {
    return ajax.get("organisation");
  },
  getDuplicateModelsFile(duplicateType) {
    return ajax.post(
      "models/duplicates/download",
      { duplicateType },
      {
        responseType: "blob",
      }
    );
  },
  getDuplicatesOverview(duplicateModelsType) {
    return ajax.get("models/duplicates", {
      params: { duplicateModelsType },
    });
  },
  getClaimReferences(esvId) {
    return ajax.get("dropdown/claimreferences", { params: { esvId } });
  },
  getImage(fileName) {
    return ajax.get("FileRepository", { params: { fileName } });
  },
  getAvailableUsersForDropdown() {
    return ajax.get("Users/AvailableUsers");
  },
  getDefaultCostClaimProcedure() {
    return ajax.get("ESV/CostClaimProcedure");
  },
  getCopyViewModel(id) {
    return ajax.get("ESV/Copy", {
      params: { id },
    });
  },
  getAffiliatesAndDistributors(esvId) {
    return ajax.get("dropdown/affiliates", {
      params: { esvId },
    });
  },
  getAvailableModels(esvId) {
    return ajax.get("appliedmodelsxmodels/available", {
      params: { esvId },
    });
  },
  getExecutionStatusses() {
    return ajax.get("dropdown/executionstatusses");
  },
  getClaimReferencesForEsv(esvId) {
    return ajax.get("dropdown/claimreferences", {
      params: { esvId },
    });
  },
  getSvNumbersForEsv(esvId) {
    return ajax.get("AppliedModels/SvNumbers", {
      params: { esvId },
    });
  },
  getClaimReferenceDetailsForEsv(esvId) {
    return ajax.get("appliedmodels/claimreferences", {
      params: { esvId },
    });
  },
  getLocalInChargePersons(esvId) {
    return ajax.get("units/InChargePersons", {
      params: { esvId },
    });
  },
  getAvailableOrganisations(esvId) {
    return ajax.get("units/Affiliates", {
      params: { esvId },
    });
  },
  getAvailableDistributionTargets(esvId) {
    return ajax.get("DistributionTargets/AvailableDistributionTargets", {
      params: { esvId },
    });
  },
  getUnit(id) {
    return ajax.get("units/details", {
      params: { id },
    });
  },
  getInternalAttachments(esvId) {
    return ajax.get("ESV/InternalAttachments", {
      params: { esvId },
    });
  },
  getEsvSituation(esvId) {
    return ajax.get("Situation", {
      params: { esvId },
    });
  },
  getEsvDistributionSituation(esvId) {
    return ajax.get("Situation/Distribution", {
      params: { esvId },
    });
  },
  getUsersForDropDown() {
    return ajax.get("dropdown/Users");
  },
  getPersonsInCharge() {
    return ajax.get("dropdown/PersonsInCharge");
  },

  getProductGroupProductSubGroups(esvId) {
    return ajax.get("Dropdown/ProductGroupProductSubGroups", {
      params: { esvId },
    });
  },
  getOrganisationTypes() {
    return ajax.get("DropDown/OrganisationTypes");
  },
  getRegionsDropDown() {
    return ajax.get("DropDown/Regions");
  },
  getApprovalValidation(esvId, validation) {
    return ajax.get("Validation", {
      params: { esvId, validation },
    });
  },
  getFactories() {
    return ajax.get("DropDown/Factories");
  },
  getFilteredFactories(){
    return ajax.get("DropDown/FilteredFactories");
  },
  getAttachments(esvId, attachmentType) {
    return ajax.get("Attachments/Overview", {
      params: { esvId, attachmentType },
    });
  },
  getAffiliateAttachments(esvId) {
    return ajax.get("Attachments/Affiliate", {
      params: { esvId },
    });
  },
  getOrganisationAttachments(esvId) {
    return ajax.get("Attachments/Organisation", {
      params: { esvId },
    });
  },
  getDistributionTargets(esvId) {
    return ajax.get("DistributionTargets", {
      params: { esvId },
    });
  },
  getDistributionTargetsAsOrganisation(esvId) {
    return ajax.get("DistributionTargets/DistributionTargetsAsOrganisation", {
      params: { esvId },
    });
  },
  getDistributionTargetsAsOrganisationByType(esvId) {
    return ajax.get(
      "DistributionTargets/DistributionTargetsAsOrganisationByType",
      {
        params: { esvId },
      }
    );
  },
  getDistributionTargetsForAttachment(id) {
    return ajax.get("Attachments/LinkedDistributionTargets", {
      params: { id },
    });
  },
  getDistributionTargetsByType(esvId) {
    return ajax.get("DistributionTargets/DistributionTargetsByType", {
      params: { esvId },
    });
  },
  getAttachmentsForDistributionTarget(distributionTargetId) {
    return ajax.get("Attachments/DistributionTarget", {
      params: { distributionTargetId },
    });
  },
  getForPublish(esvId) {
    return ajax.get("Distribution", {
      params: { esvId },
    });
  },
  getServiceRequestTypesIndex() {
    return ajax.get("ServiceRequestTypes/Index");
  },
  getServiceRequestTypesUpdate() {
    return ajax.get("ServiceRequestTypes/Update");
  },
  getLogAlternativeLogins({ page, itemsPerPage, sortBy, sortDesc }, search) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("LogAlternativeLogins/LoadOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getLockedESVs({ page, itemsPerPage, sortBy, sortDesc }, search) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("LockedESV/LoadOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getTranslatableEsvs(
    {
      options: { page, itemsPerPage, sortBy, sortDesc },
      search,
      ignoreServiceLimitTime,
    },
    cultureId
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("Translations", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        cultureId,
        ignoreServiceLimitTime,
      },
    });
  },
  getTranslationDetails(id, selectedCultureId) {
    return ajax.get("Translations/Details", {
      params: { id, selectedCultureId },
    });
  },
  getModelsOverview({ page, itemsPerPage, sortBy, sortDesc }, search) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("Models", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getPartsOverview(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    cultureId
  ) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("Parts/LoadOverview", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        cultureId,
      },
    });
  },
  getPartDetailsById(partId) {
    return ajax.get("parts/update", {
      params: {
        partId,
      },
    });
  },
  getModelDetailsById(id) {
    return ajax.get("models/details", {
      params: {
        id,
      },
    });
  },

  getCultures() {
    return ajax.get("Dropdown/Cultures");
  },
  getDossierNumbersForServiceRequestType(serviceRequestTypeId) {
    return ajax.get("Copy/Dossiers", {
      params: { serviceRequestTypeId },
    });
  },
  getVersionsForDossier(original) {
    return ajax.get("Copy/Versions", {
      params: original,
    });
  },
  getAvailableVersionNumbers(original) {
    return ajax.get("Copy/AvailableVersionNumbers", {
      params: original,
    });
  },
  getRevisionsForDossier(original) {
    return ajax.get("Copy/Revisions", {
      params: original,
    });
  },
  getLatestRevisionForCopy(original) {
    return ajax.get("Copy/LatestRevision", {
      params: original,
    });
  },

  getNextRevisionForCopyForNewReference(originalRelated) {
    return ajax.get("Copy/NextRevisionForNewReference", {
      params: originalRelated,
    });
  },

  getTitleForRevision(original) {
    return ajax.get("Copy/GetTitleForRevision", {
      params: original,
    });
  },
  requestPasswordReset(credentials) {
    return ajax.get("Authentication/ResetPassword", { params: credentials });
  },
  getUserDetailsForReset(hash) {
    return ajax.get("Authentication/ResetPasswordDetails", {
      params: { hash },
    });
  },
  getUserDetailsForSet(hash) {
    return ajax.get("Authentication/SetPasswordDetails", {
      params: { hash },
    });
  },
  getAppliedModelXParts(esvId, { modelId, serialFrom, serialTo }) {
    return ajax.get("Parts/PartsAndLabourForModel", {
      params: {
        esvId,
        modelId,
        serialFrom,
        serialTo,
      },
    });
  },
  getManuals({ page, itemsPerPage, sortBy, sortDesc }, search) {
    let searchValues = [];
    searchValues.push(search);
    let searchKeys = [];
    searchKeys.push("name");

    return ajax.get("Help/GetManuals", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getLoggedUpdates({ page, itemsPerPage, sortBy, sortDesc }, search) {
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax.get("LoggedUpdates", {
      paramsSerializer: function(params) {
        return QS.stringify(params);
      },
      params: {
        searchKeys,
        searchValues,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
      },
    });
  },
  getEsvsForDropdown() {
    return ajax.get("Dropdown/Esvs");
  },
  searchModelItemForAutofill(productNumber) {
    return ajax.get("Models/SearchModelItemForAutocomplete", {
      params: { productNumber },
    });
  },
  searchPartForAutofill(number) {
    return ajax.get("Parts/SearchPartForAutocomplete", {
      params: { number },
    });
  },
  getLoginOption(email) {
    return ajax.get("Authentication/LoginOption", {
      params: { email },
    });
  },
  async getTokenForAAD(token) {
    var form = new FormData();
    form.append("token", token);
    const resp = await ajax.post("Authentication/AADToken", form);
    console.log("AADToken Response:", resp.data); // Debugging line
    if (resp.status === 200) {
      const authResult = resp.data.authenticationResult;
      const settings = resp.data.serviceRequestTypeSettings;
  
      if (authResult && authResult.token) {
        store.commit("login", authResult);
        store.commit("setServiceRequestTypeSettings", settings);
        ajax.defaults.headers.common["Authorization"] = authResult.token;
      } else {
        console.error("authenticationResult or token is missing in the AADToken response.");
      }
    }
    return resp;
  },
  
  async login(credentials, skipToken) {
    const resp = await ajax.post("authentication", credentials);
    console.log("Login Response:", resp.data); // Debugging line
    if (resp.status === 200 && !skipToken) {
      const authResult = resp.data.authenticationResult;
      const settings = resp.data.serviceRequestTypeSettings;
  
      if (authResult && authResult.token) {
        store.commit("login", authResult);
        store.commit("setServiceRequestTypeSettings", settings);
        ajax.defaults.headers.common["Authorization"] = authResult.token;
      } else {
        console.error("authenticationResult or token is missing in the login response.");
      }
    }
    return resp;
  },
  
  
  // createIpWhiteList(ipwhitelist) {
  //   return ajax.post('ipwhitelist/create', ipwhitelist)
  // },
  createUser(user) {
    return ajax.post("users/create", user);
  },
  createOrganisation(organisation) {
    return ajax.post("organisation/create", organisation);
  },
  updateOrganisation(organisation) {
    return ajax.post("organisation/update", organisation);
  },
  createServiceRequestTypes(model) {
    return ajax.post("serviceRequestTypes/create", model);
  },

  unlockLockedEsv(esv) {
    return ajax.post("LockedESV/UnlockESV", esv);
  },

  // updateUser(user){
  //   return ajax.post('users/update', user)
  // },
  postCorrectedDuplicateModels(formData) {
    return ajax.post("models/duplicates/upload", formData, {
      responseType: "blob",
    });
  },
  sendMarkAsCorrect(modelId, duplicateModelIds) {
    return ajax.post("models/duplicates/markAsCorrect", {
      modelId,
      duplicateModelIds,
    });
    // return ajax.post('models/duplicates/markAsCorrect?modelId='+ modelId
    // +'&duplicateModelIdConcatenation=' + duplicateModelIdConcatenation)
  },
  requestAuthenticationTicket(credentials) {
    return ajax.post("authentication/ticket", credentials);
  },
  postPart(part) {
    return ajax.post("parts", part);
  },
  addUnit(unit) {
    return ajax.post("units", unit);
  },
  addInternalAttachments(esvId, files) {
    var formData = new FormData();
    formData.append("esvId", esvId);
    files.forEach((file) => {
      formData.append("files", file);
    });
    // formData.append("files", files);

    return ajax.post("ESV/InternalAttachments", formData);
  },
  postNewDocument(document) {
    return ajax.post("ESV", document);
  },
  addRule(rule) {
    return ajax.post("ECareCommunicationRules/AddRule", rule);
  },
  updateRule(id, rule) {
    return ajax.post(`ECareCommunicationRules/updateRule/${id}`, rule);
  },
  getRuleById(id) {
    return ajax.get("ECareCommunicationRules/RuleDetails", {
      params: { id },
    });
  },
  startApproval(id) {
    const esvId = parseInt(id);
    var formData = new FormData();
    formData.append("esvId", esvId);
    return ajax.post("Approval/Start", formData);
  },
  addAttachment(esvId, organisationId, attachmentType, files) {
    var formData = new FormData();
    formData.append("EsvId", esvId);
    formData.append("OrganisationId", organisationId);
    formData.append("AttachmentType", attachmentType);
    files.forEach((file) => {
      formData.append("files", file);
    });

    return ajax.post("Attachments", formData);
  },
  addOrganisationAttachment(esvId, organisationId, attachmentType, files) {
    var formData = new FormData();
    formData.append("EsvId", esvId);
    formData.append("OrganisationId", organisationId);
    formData.append("AttachmentType", attachmentType);
    files.forEach((file) => {
      formData.append("files", file);
    });

    return ajax.post("Attachments/add-and-link", formData);
  },
  uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return ajax.post(`images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  approve(id) {
    const esvId = parseInt(id);
    var formData = new FormData();
    formData.append("esvId", esvId);
    return ajax.post("Approval", formData);
  },
  reject(esvId, rejectionReason) {
    return ajax.post("Approval/Reject", {
      esvId,
      rejectionReason,
    });
  },
  logout() {
    return ajax.post("Authentication/Logout");
  },
  copy(model, useSPCopy = false) {
    return ajax.post(`Copy/Copy?useSPCopy=${useSPCopy}`, model);
  },
  updateTranslations(translation, cultureId) {
    translation.supportedCultureId = cultureId;
    return ajax.post("Translations", translation);
  },

  //DELETE
  // deleteIpWhiteList(id){
  //   return ajax.delete('ipwhitelist/delete', {
  //     params: {
  //       id
  //     }
  //   })
  // },
  deleteUser(id, reactivate) {
    return ajax.delete("users/delete", {
      params: {
        id,
        reactivate,
      },
    });
  },
  deleteAppliedModelsXModel(id, objectId) {
    return ajax.delete("appliedmodelsxmodels", {
      params: {
        id,
        objectId,
      },
    });
  },
  deleteAppliedModel(id) {
    return ajax.delete("appliedmodels", {
      params: {
        id,
      },
    });
  },
  deleteSparePart(id) {
    return ajax.delete("parts", {
      params: {
        id,
      },
    });
  },
  deleteESV(id) {
    return ajax.delete("ESV", {
      params: {
        id,
      },
    });
  },
  removeInternalAttachment(id) {
    return ajax.delete("ESV/InternalAttachments", {
      params: { id },
    });
  },
  removeAttachment(esvId, id) {
    return ajax.delete("Attachments", {
      params: { esvId, id },
    });
  },
  deleteUnit(id) {
    return ajax.delete("units", {
      params: {
        id,
      },
    });
  },
  deleteAllUnits(esvId) {
    return ajax.delete("units/All", {
      params: {
        esvId,
      },
    });
  },
  deleteAllExclusions(esvId) {
    return ajax.delete("AppliedModelsXModels/Exclusions", {
      params: { esvId },
    });
  },
  deleteAllAppliedModelsXModels(esvId) {
    return ajax.delete("AppliedModelsXModels/All", {
      params: { esvId },
    });
  },

  removePartFromAppliedModel(appliedModelXPart) {
    return ajax.delete("Parts/AppliedModelXPart", {
      params: {
        appliedModelXPartId: appliedModelXPart.id,
        objectId: appliedModelXPart.objectId,
      },
    });
  },
  deletePartFromScope(esvId, appliedModelXPartId, objectId) {
    return ajax.delete("Parts/PartInScope", {
      params: {
        esvId,
        appliedModelXPartId,
        objectId,
      },
    });
  },
  decline(esvId) {
    return ajax.delete("Distribution", {
      params: { esvId },
    });
  },
  deleteCommunicationRule(Id) {
    return ajax.delete("ECareCommunicationRules/DeleteRule", {
      params: { id: Id } 
    });
  },

  //PUT
  // updateIpWhiteList(ipwhitelist) {
  //   return ajax.put('ipwhitelist/update', ipwhitelist)
  // },
  updateUser(user) {
    return ajax.put("users/update", user);
  },
  updateUserProfile(user) {
    return ajax.put("users/profile", user);
  },
  updatePart(part) {
    return ajax.put("parts/update", part);
  },
  putAppliedModelsXModelUpdate(appliedModel) {
    return ajax.put("appliedmodelsxmodels", appliedModel);
  },
  putAppliedModelUpdate(appliedModel) {
    return ajax.put("appliedmodels", appliedModel);
  },
  updateServiceRequestTypes(model) {
    return ajax.put("serviceRequestTypes/update", model);
  },
  putEsvUpdate(esv, startApproval = false) {
    return ajax.put(`esv?startApproval=${startApproval}`, esv, {
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  putSparePartUpdate(sparepart) {
    return ajax.put("parts", sparepart);
  },
  putInternalDocuments(internalDocuments) {
    return ajax.put("esv/internaldocuments", internalDocuments, {
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  activateTicket(ticketId) {
    var formData = new FormData();
    formData.append("ticketId", ticketId);
    return ajax.put("authentication/ticket/activate", formData);
  },
  addPartToAppliedModel(appliedModelXPart) {
    return ajax.put("parts", appliedModelXPart);
  },
  updateUnit(unit) {
    return ajax.put("units", unit, {
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  updateEsvSituation(situation) {
    return ajax.put("situation", situation, {
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  releaseLock(id) {
    return ajax.get("ESV/ReleaseLock", {
      params: { id },
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  releaseAllLocksForUser() {
    return ajax.get("ESV/ReleaseAllLocksForUser", {
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  lock(id, force) {
    store.commit("setTokenId");
    return ajax.get("ESV/Lock", {
      params: { id, force },
      raxConfig: noRetryConfig,
      headers: { "X-ESV-TokenId": store.state.tokenId },
    });
  },
  linkDistributionTargetsToAttachment(id, distributionTargetIds) {
    return ajax.put("Attachments", {
      id,
      distributionTargetIds,
    });
  },
  updateDistributionTargetsForEsv(esvId, distributionTargets) {
    return ajax.put("DistributionTargets", {
      esvId,
      distributionTargets,
    });
  },
  publish(model) {
    return ajax.put("Distribution", model);
  },
  resetPassword(credentials) {
    return ajax.put("Authentication/ResetPassword", {
      email: credentials.email,
      passwordHash: credentials.password,
      telephone: credentials.telephone,
    });
  },
  updateModel(model) {
    return ajax.put("Models", model);
  },

  generateDossierForRns(serviceRequestTypeId) {
    return ajax.get("ESV/GenerateDossierForRns", {
      params: { serviceRequestTypeId },
    });
  },
  //EXPORT
  exportLogsTechnical(search) {
  const params = {
    fromDate: search.fromDate,
    toDate: search.toDate,
    responseStatus: search.responseStatus,
    country: search.country,
    salesOrg: search.salesOrganisation,
    organisationId: search.organisation,
    sbmProfileId: search.sbmProfile,
  };

  console.log("📤 Export params:", params);

  return ajax.get("ECareLogs/export/technical", {
    params,
    responseType: "blob"
  })
  .then(resp => {
    const iso = new Date().toISOString();
    const [date, time] = iso.split("T");
    const [yyyy, mm, dd] = date.split("-");
    const [hh, min, ss] = time.split(":");

    const fileName = `ECare_API_Logs_${yyyy}${mm}${dd}_${hh}${min}${ss}.xlsx`;

    const url = window.URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  });
},
  downloadAttachment(id) {
    return ajax
      .get("Attachments", {
        params: { id },
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },
  downloadInternalAttachment(id) {
    return ajax
      .get("Attachments/Internal", {
        params: { id },
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },

  downloadDetails(id, exportType) {
    return ajax
      .get("Export/Details", {
        params: {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },
  downloadInternalDocuments(id, exportType) {
    return ajax
      .get("Export/InternalDocuments", {
        params: {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },
  downloadModelsAndParts(id, exportType) {
    return ajax
      .post(
        "Export/ModelsAndParts",
        {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadExclusionRanges(id, exportType) {
    return ajax
      .post(
        "Export/ExclusionRanges",
        {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadModelTemplate(id, exportType) {
    return ajax
      .post(
        "Export/ModelTemplate",
        {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadUnitsInScopeTemplate(id, exportType) {
    return ajax
      .post(
        "Export/UnitsInScopeTemplate",
        {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadPartTemplate(id, exportType) {
    return ajax
      .post(
        "Export/PartTemplate",
        {
          id,
          exportType,
          selectedCultureId: store.state.user.selectedCultureId,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadOverview(exportType, statusCode, search, ignoreServiceLimitTime) {
    var data = {
      exportType,
      statusCode,
    };
    if (search) {
      var searchKeys = Object.keys(search);
      var searchValues = this.mapSearchValues(search);
      data.search = {
        searchKeys: searchKeys,
        searchValues: searchValues,
        ignoreServiceLimitTime: ignoreServiceLimitTime,
      };
    }

    return ajax
      .post("Export/Overview", data, {
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },
  downloadAdvancedSearchData(
    { page, itemsPerPage, sortBy, sortDesc },
    search,
    ignoreServiceLimitTime
  ) {
    //var search = this.mergeTwoArraysTogetherToAnObject(searchTable, searchAdvanced);
    var searchKeys = Object.keys(search);
    var searchValues = this.mapSearchValues(search);

    return ajax
      .post(
        "Export/AdvancedSearch",
        {
          searchKeys,
          searchValues,
          page,
          itemsPerPage,
          sortBy,
          sortDesc,
          ignoreServiceLimitTime,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadUnits(id, exportType, request) {
    console.log(id, exportType, request);
    var searchKeys = Object.keys(request.search);
    var searchValues = this.mapSearchValues(request.search);
    console.log(searchKeys, searchValues);
    return ajax
      .post(
        "Export/Units",
        {
          id,
          exportType,
          search: { searchKeys, searchValues },
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  downloadInternalInfoOverview(payload) {
    return ajax.post(
      "Export/InternalInfoOverview",
      payload,
      {
        responseType: "blob",
      }
    )
    .then((resp) => Common.download(resp));
  },
  downloadCRM(id, exportType) {
    return ajax
      .post(
        "Export/CRM",
        {
          id,
          exportType,
        },
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  exportUserImportTemplate() {
    return ajax
      .post(
        "Users/ExportUserImportTemplate",
        {},
        {
          responseType: "blob",
        }
      )
      .then((resp) => Common.download(resp));
  },
  exportUserOverview(showAll) {
    return ajax
      .get("Users/ExportUserOverview?showAll=" + showAll, {
        responseType: "blob",
      })
      .then((resp) => Common.download(resp));
  },
  downloadManual(id) {
    return (
      ajax
        .get("Help/Download/" + id, {
          responseType: "blob",
        })
        //return ajax.get('Help/Download/'+ id)
        .then((resp) => Common.download(resp))
    );
  },

  //IMPORT

  postExclusionTemplate(formData) {
    return ajax.post(
      "upload/ExclusionTemplate",
      formData
      // ,{
      //   responseType: 'blob',
      //   // cancelToken: new CancelToken(function executor(c){
      //   //   bomCancelTokens.push(c); // = c;
      //   // })
      // }
    );
  },
  postUpdatedModelDefinitions(formData) {
    return ajax.post(
      "upload/ModelTemplate",
      formData
      // ,{
      //   responseType: 'blob',
      //   // cancelToken: new CancelToken(function executor(c){
      //   //   bomCancelTokens.push(c); // = c;
      //   // })
      // }
    );
  },
  postImportUsersData(formData) {
    return ajax.post("users/ImportUsers", formData);
  },
  postUnitsTemplate(formData) {
    return ajax.post("upload/ExecutionFollowUp", formData);
  },
  postUnitsUpdateTemplate(formData) {
    return ajax.post("upload/ExecutionFollowUpUpdate", formData);
  },
  postPartsTemplate(formData) {
    return ajax.post("upload/PartTemplate", formData);
  },
  postCrmPslAndTemplates(formData) {
    return ajax.post("Upload/CrmPslAndTemplates", formData);
  },

  //HELPING HAND
  mergeTwoArraysTogetherToAnObject(array1, array2) {
    let outputArray = { ...array1, ...array2 };

    return outputArray;
  },

  mapSearchValues(search) {
    return Object.values(search).map((value) => {
      return value ? value.toString() : null;
    });
  },

  fetchImage(imageUrl) {
    // The imageUrl should already contain the pattern identifier in the path
    // Format: /api/Images/daikin-secure/{uuid}.{extension}
    
    // Extract the UUID and extension from the URL
    const match = imageUrl.match(/\/api\/Images\/daikin-secure\/([^/]+)$/);
    
    if (match && match[1]) {
      // Use the standard API call pattern with the extracted UUID
      return ajax.get(`Images/daikin-secure/${match[1]}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });
    } else {
      // Fallback to the original URL if pattern doesn't match
      console.warn('Image URL does not match expected pattern:', imageUrl);
      // Remove leading slash if present since ajax will add it
      const url = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
      return ajax.get(url, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });
    }
  },
};
